/**
 * CustomOverlayTrigger - Tooltip trigger component using Radix UI
 *
 * This component wraps children and shows a tooltip on hover/focus.
 * It provides API compatibility with the old react-bootstrap OverlayTrigger.
 */
import React, { cloneElement, PropsWithChildren } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

interface OverlayTriggerProps extends PropsWithChildren {
  defaultOverlayShown?: boolean;
  trigger?: string[];
  placement?: "top" | "bottom" | "left" | "right";
  overlay: React.ReactElement;
  delayHide?: number;
  delayShow?: number;
  delay?: number;
  onBlur?: (e: React.FocusEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onMouseOut?: (e: React.MouseEvent) => void;
  onMouseOver?: (e: React.MouseEvent) => void;
}

function OverlayTrigger({
  placement = "top",
  overlay,
  children,
  delayShow = 0,
  delayHide = 0,
  delay = 0,
  trigger = ["hover", "focus"],
}: OverlayTriggerProps) {
  const child = React.Children.only(children) as React.ReactElement;

  // Convert placement to Radix side
  const side =
    placement === "top"
      ? "top"
      : placement === "bottom"
        ? "bottom"
        : placement === "left"
          ? "left"
          : "right";

  // Extract tooltip content from the overlay prop
  const tooltipContent =
    overlay.props.children || overlay.props.content || overlay;

  // Calculate delay
  const openDelay = delayShow || delay || 0;
  // closeDelay is available for future use if needed
  const _closeDelay = delayHide || delay || 0;
  void _closeDelay; // Suppress unused warning

  // Check if we should use click trigger
  const isClickTrigger =
    Array.isArray(trigger) &&
    trigger.includes("click") &&
    !trigger.includes("hover");

  if (isClickTrigger) {
    // For click triggers, use a different approach with state
    return <ClickTooltip side={side} content={tooltipContent} child={child} />;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={openDelay}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{child}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={8}
            className={cn(
              "z-50 rounded-lg bg-[#1a1a1a] px-3 py-2 text-xs font-semibold leading-4 text-white text-center max-w-xs",
              "animate-in fade-in-0 zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "data-[side=top]:slide-in-from-bottom-2",
              "data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2"
            )}
          >
            {tooltipContent}
            <TooltipPrimitive.Arrow className="fill-[#1a1a1a]" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

// Separate component for click-triggered tooltips
function ClickTooltip({
  side,
  content,
  child,
}: {
  side: "top" | "bottom" | "left" | "right";
  content: React.ReactNode;
  child: React.ReactElement;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger asChild>
          {cloneElement(child, {
            onClick: (e: React.MouseEvent) => {
              child.props.onClick?.(e);
              setOpen((prev) => !prev);
            },
          })}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={8}
            className={cn(
              "z-50 rounded-lg bg-[#1a1a1a] px-3 py-2 text-xs font-semibold leading-4 text-white text-center max-w-xs",
              "animate-in fade-in-0 zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "data-[side=top]:slide-in-from-bottom-2",
              "data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2"
            )}
            onPointerDownOutside={() => setOpen(false)}
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-[#1a1a1a]" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export default OverlayTrigger;
